import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import i18n from "i18n";
import { Badge, Button, Card, CardBody, CardTitle, Col, Input, Row } from "reactstrap";
import { ErrorToaster, successToaster } from "components/common/toast";
import { UserContextProvider } from "context/UserContext";
import { AddCicApi, DeleteCicApi, GetCicApi, UpdateCicApi } from "network/user";
import CategoryTableCic from "views/products/CategoryTableCic";

// Collegamento con la cassa CassaInCloud: chiave API, punto vendita, webhook e importazione prodotti.
const errText = (e) => {
  const m = e?.message || e?.data?.message || i18n.t("account:cic.failed");
  // errori tecnici di rete verso CassaInCloud: messaggio comprensibile
  return /HTTPSConnectionPool|Request failed|Max retries/i.test(m)
    ? i18n.t("account:cic.networkError")
    : m;
};
// Se il server non ha l'indirizzo pubblico configurato (HOST_URL = localhost), l'URL del webhook
// esce con localhost: lo ricostruisce con l'indirizzo reale del backend usato dal pannello.
const API_ORIGIN = (() => {
  try {
    return new URL(process.env.REACT_APP_BASE_URL).origin;
  } catch (e) {
    return "";
  }
})();
export const publicWebhookUrl = (url) => {
  if (!url) return "";
  try {
    const u = new URL(url);
    if (API_ORIGIN && /^(localhost|127\.0\.0\.1|0\.0\.0\.0|\[::1\])$/i.test(u.hostname)) return `${API_ORIGIN}${u.pathname}`;
  } catch (e) {
    return url;
  }
  return url;
};
const mask = (k) => (k ? `${"•".repeat(Math.max(0, Math.min(k.length - 4, 24)))}${k.slice(-4)}` : "");

const CicSettings = () => {
  const { t } = useTranslation("account");
  const { fetchUser } = useContext(UserContextProvider);
  const [cfg, setCfg] = useState(null); // null = caricamento, {} = non collegato
  const [editing, setEditing] = useState(false);
  const [apiKey, setApiKey] = useState("");
  const [salesPoint, setSalesPoint] = useState("");
  const [secret, setSecret] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () =>
    GetCicApi()
      .then((r) => {
        const d = r?.data && !Array.isArray(r.data) ? r.data : {};
        setCfg(d);
        setSalesPoint(d.sales_point_id || "");
      })
      .catch((e) => {
        setCfg({});
        ErrorToaster(errText(e));
      });

  useEffect(() => {
    load();
  }, []);

  const connected = !!cfg?.api_key;

  const saveKey = async () => {
    if (!connected && !apiKey.trim()) return ErrorToaster(t("cic.enterKey"));
    if (!salesPoint.trim()) return ErrorToaster(t("cic.enterSalesPoint"));
    setBusy(true);
    try {
      const payload = { sales_point_id: salesPoint.trim() };
      if (apiKey.trim()) payload.api_key = apiKey.trim();
      if (connected) await UpdateCicApi(payload);
      else await AddCicApi(payload);
      successToaster(connected ? t("cic.updated") : t("cic.connectedToast"));
      setApiKey("");
      setEditing(false);
      fetchUser && fetchUser();
      load();
    } catch (e) {
      ErrorToaster(errText(e));
    } finally {
      setBusy(false);
    }
  };

  const saveSecret = async () => {
    if (!secret.trim()) return ErrorToaster(t("cic.enterSecret"));
    setBusy(true);
    try {
      await UpdateCicApi({ webhook_secret: secret.trim() });
      successToaster(t("cic.webhookActive"));
      setSecret("");
      load();
    } catch (e) {
      ErrorToaster(errText(e));
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm(t("cic.confirmDisconnect"))) return;
    try {
      await DeleteCicApi();
      successToaster(t("cic.disconnected"));
      fetchUser && fetchUser();
      setCfg({});
      setSalesPoint("");
    } catch (e) {
      ErrorToaster(errText(e));
    }
  };

  const copy = (text) => {
    navigator.clipboard?.writeText(text).then(() => successToaster(t("cic.copied")), () => ErrorToaster(t("cic.copyFailed")));
  };

  if (cfg === null) return <div className="content"><p>{t("cic.loading")}</p></div>;

  return (
    <div className="content">
      <Row>
        <Col md="12">
          <Card>
            <CardBody>
              <div className="d-flex justify-content-between align-items-start flex-wrap" style={{ gap: 12 }}>
                <div>
                  <CardTitle tag="h4" className="mb-1">{t("cic.step1Title")}</CardTitle>
                  <p className="text-muted small mb-0">
                    {t("cic.step1Text")}
                  </p>
                </div>
                <Badge color={connected ? "success" : "secondary"}>{connected ? t("cic.connected") : t("cic.notConnected")}</Badge>
              </div>

              {connected && !editing ? (
                <div className="mt-3">
                  <div className="mb-1"><span className="text-muted">{t("cic.apiKeyLabel")}</span> <code>{mask(cfg.api_key)}</code></div>
                  <div className="mb-3"><span className="text-muted">{t("cic.salesPointLabel")}</span> <b>{cfg.sales_point_id}</b></div>
                  <Button onClick={() => setEditing(true)}>{t("cic.changeKey")}</Button>
                  <Button color="danger" onClick={disconnect}>{t("cic.disconnect")}</Button>
                </div>
              ) : (
                <Row className="mt-3">
                  <Col md="6" className="mb-3">
                    <label>{t("cic.apiKeyField")} {connected && <span className="text-muted">{t("cic.keepCurrent")}</span>}</label>
                    <Input value={apiKey} onChange={(e) => setApiKey(e.target.value)} autoComplete="off" placeholder={t("cic.apiKeyPlaceholder")} />
                  </Col>
                  <Col md="3" className="mb-3">
                    <label>{t("cic.salesPointField")}</label>
                    <Input value={salesPoint} onChange={(e) => setSalesPoint(e.target.value)} />
                  </Col>
                  <Col md="12">
                    <Button color="primary" onClick={saveKey} disabled={busy}>{busy ? t("cic.verifying") : connected ? t("cic.save") : t("cic.connect")}</Button>
                    {connected && <Button onClick={() => setEditing(false)} disabled={busy}>{t("cic.cancel")}</Button>}
                    <div className="small text-muted mt-1">{t("cic.verifyHint")}</div>
                  </Col>
                </Row>
              )}
            </CardBody>
          </Card>

          {connected && (
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-start flex-wrap" style={{ gap: 12 }}>
                  <div>
                    <CardTitle tag="h4" className="mb-1">{t("cic.step2Title")}</CardTitle>
                    <p className="text-muted small mb-0">
                      {t("cic.step2Text")}
                    </p>
                  </div>
                  <Badge color={cfg.webhook_configured ? "success" : "warning"}>{cfg.webhook_configured ? t("cic.active") : t("cic.toComplete")}</Badge>
                </div>
                <ol className="mt-3 mb-3 pl-3">
                  <li className="mb-2">
                    {t("cic.copyAddress")}
                    <div className="d-flex mt-1" style={{ gap: 8, maxWidth: 760 }}>
                      <Input readOnly value={publicWebhookUrl(cfg.webhook_url)} onFocus={(e) => e.target.select()} />
                      <Button className="m-0 text-nowrap" style={{ flexShrink: 0 }} onClick={() => copy(publicWebhookUrl(cfg.webhook_url))}>{t("cic.copy")}</Button>
                    </div>
                  </li>
                  <li className="mb-2">
                    {t("cic.openPre")}<b>{t("cic.openPath")}</b>{t("cic.openMid")}
                    {" "}<b>{t("cic.eventStock")}</b>{t("cic.eventStockDetail")}<b>{t("cic.eventProduct")}</b>{t("cic.eventProductDetail")}
                  </li>
                  <li>
                    {t("cic.secretPre")}<b>{t("cic.secretWord")}</b>{t("cic.secretPost")}
                    <div className="d-flex mt-1" style={{ gap: 8, maxWidth: 760 }}>
                      <Input value={secret} onChange={(e) => setSecret(e.target.value)} autoComplete="off"
                        placeholder={cfg.webhook_configured ? t("cic.secretSaved") : t("cic.secretPlaceholder")} />
                      <Button color="primary" className="m-0 text-nowrap" style={{ flexShrink: 0 }} onClick={saveSecret} disabled={busy}>{t("cic.save")}</Button>
                    </div>
                  </li>
                </ol>
              </CardBody>
            </Card>
          )}

          {connected && (
            <Card>
              <CardBody>
                <CardTitle tag="h4" className="mb-1">{t("cic.step3Title")}</CardTitle>
                <p className="text-muted small">{t("cic.step3Text")}</p>
                <CategoryTableCic />
              </CardBody>
            </Card>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default CicSettings;
